'use client';
import { useStore } from '@/providers/app-store-provider';
import { ReportingCenter } from '@/reporting';
import { ImpactRadiusProvider } from '@/reporting/providers/impactProvider';
import { ProviderType } from '@/reporting/types';
import { useWhenReady } from '@/hooks/useWhenReady';
import logger from '@/utils/client-logger';
import { useRef } from 'react';

/**
 * Impact 绑定初始化 Hook
 * 主要用于页面初始化时处理 Impact Radius 参数绑定
 * - 游客用户：存储 irclickid 参数到 localStorage（30天）
 * - 登录用户：存储参数 + 调用 API 绑定（但用户登录后的绑定主要由 userSlice.ts 处理）
 */
export const useImpactBindInit = () => {
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const userInfoReady = useStore((state) => state.userInfoReady);
  const userInfo = useStore((state) => state.userInfo);
  const visitorId = useStore((state) => state.visitorId);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const isAuthenticatedRef = useRef(Boolean(token));
  const bindingStartedRef = useRef(false);
  // 读取执行绑定时的最新登录态，但 token 变化本身不重复触发全局初始化绑定。
  isAuthenticatedRef.current = Boolean(token);
  // 首次就绪及身份变化时识别；已有登录态需等待账户信息，避免先发送游客身份。
  useWhenReady(
    clientTokenReady && Boolean(visitorId) && (!token || userInfoReady),
    () => {
      const impactProvider = ReportingCenter.getInstance().getProvider(
        ProviderType.IMPACT_RADIUS,
      ) as ImpactRadiusProvider;
      impactProvider?.identify(token ? userInfo : {}, visitorId);
    },
    [visitorId, token, userInfo.id, userInfo.email],
  );
  // 点击参数在落地时读取；绑定不等待账户信息，避免软导航后丢失 URL 中的 irclickid。
  useWhenReady(clientTokenReady, () => {
    if (bindingStartedRef.current) return;
    const handleImpactBinding = async () => {
      try {
        const isAuthenticatedAtBind = isAuthenticatedRef.current;
        const center = ReportingCenter.getInstance();
        const impactProvider = center.getProvider(
          ProviderType.IMPACT_RADIUS,
        ) as ImpactRadiusProvider;

        if (impactProvider) {
          bindingStartedRef.current = true;
          // 直接使用封装好的bindImpact方法，它会自动处理：
          // 1. 从URL参数或localStorage获取irclickid
          // 2. URL带新irclickid时设置30天过期时间（游客和登录用户都需要）
          // 3. 调用后端API绑定（用户登录后的绑定主要由userSlice.ts处理）
          // 4. 游客保留irclickid用于注册下单；登录态绑定成功后按原逻辑清理
          await impactProvider.bindImpact(
            () => {
              if (isAuthenticatedAtBind) {
                refreshAccountInfo();
              }
            },
            { clearIrclickidOnSuccess: isAuthenticatedAtBind },
          );
        }
      } catch (error) {
        logger.error('Failed to handle impact binding:', error);
      }
    };

    handleImpactBinding();
  }, [refreshAccountInfo]); // 依赖 refreshAccountInfo
};
